/**
 * Responsibility: decide what order holdings appear in.
 *
 * Separate from the table's own column sorting, and not a replacement for it.
 * Clicking a column head is the fastest way to reorder a table, but the
 * collection is also shown as a grid of card images, and a grid has no column
 * heads to click. An explicit order applies to both, so switching view does
 * not silently change what you are looking at.
 *
 * **Grade sorts by its number, not its label.** Sorting on the text put "PSA
 * 10" above "PSA 9" only by luck of the alphabet, and "Grade 9.5" nowhere near
 * either. The number is parsed out and compared, so highest-first means what
 * it says.
 *
 * **Ungraded copies sort last, whichever direction is chosen.** A raw card has
 * no grade - not a zero - so it cannot be the lowest-graded card any more than
 * it can be the highest. Sinking them to the bottom keeps "best grade first"
 * answerable without pretending a raw copy scored nought.
 *
 * **A grade is not a total order, so it needs a second key.** Half a
 * collection can be PSA 10, and comparing two of them returns nothing to
 * sort on - so they came back in whatever order the server happened to send,
 * which reads as an unsorted list sitting inside a sorted one. Value breaks
 * the tie, in the same direction as the grade: "highest first" means the
 * best grade and, within it, the most valuable.
 */
import type { CollectionItem } from '../../api/client';
import { gradeNumber } from './collectionFilters';

export type CollectionSort =
  | 'added-desc'
  | 'added-asc'
  | 'value-desc'
  | 'value-asc'
  | 'grade-desc'
  | 'grade-asc'
  | 'name-asc'
  | 'set-asc';

export const DEFAULT_SORT: CollectionSort = 'added-desc';

/** Menu wording, in the order the menu should offer them. */
export const SORT_OPTIONS: { value: CollectionSort; label: string }[] = [
  { value: 'added-desc', label: 'Newest first' },
  { value: 'added-asc', label: 'Oldest first' },
  { value: 'value-desc', label: 'Value, highest first' },
  { value: 'value-asc', label: 'Value, lowest first' },
  { value: 'grade-desc', label: 'Grade, highest first' },
  { value: 'grade-asc', label: 'Grade, lowest first' },
  { value: 'name-asc', label: 'Card name, A to Z' },
  { value: 'set-asc', label: 'Set, A to Z' },
];

/** Money as a number, or null when the holding has never been priced. */
function valueOf(item: CollectionItem): number | null {
  return item.holding_value === null ? null : Number(item.holding_value);
}

/** A grade only counts when something actually graded the card. */
function gradeOf(item: CollectionItem): number | null {
  return item.is_graded ? gradeNumber(item.grade_label) : null;
}

/**
 * Compare two values where null means "cannot be ranked".
 *
 * Null always sorts last. Returning 0 between two nulls keeps the sort
 * stable, so unpriced cards stay in whatever order they already had rather
 * than shuffling every time the list is re-sorted.
 */
function nullsLast(a: number | null, b: number | null, direction: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return (a - b) * direction;
}

/**
 * Grade first, then value within it.
 *
 * The second key also orders the ungraded block at the bottom, which is
 * otherwise the largest group of all and the most obviously unsorted.
 */
function byGradeThenValue(direction: 1 | -1) {
  return (a: CollectionItem, b: CollectionItem): number => {
    const byGrade = nullsLast(gradeOf(a), gradeOf(b), direction);
    if (byGrade !== 0) return byGrade;
    return nullsLast(valueOf(a), valueOf(b), direction);
  };
}

const COMPARATORS: Record<CollectionSort, (a: CollectionItem, b: CollectionItem) => number> = {
  'added-desc': (a, b) => b.added_at.localeCompare(a.added_at),
  'added-asc': (a, b) => a.added_at.localeCompare(b.added_at),
  'value-desc': (a, b) => nullsLast(valueOf(a), valueOf(b), -1),
  'value-asc': (a, b) => nullsLast(valueOf(a), valueOf(b), 1),
  'grade-desc': byGradeThenValue(-1),
  'grade-asc': byGradeThenValue(1),
  'name-asc': (a, b) => a.name.localeCompare(b.name),
  // Cards with no set land together at the end rather than under an empty
  // heading, which is what an empty string would sort as.
  'set-asc': (a, b) => (a.set_label ?? '￿').localeCompare(b.set_label ?? '￿'),
};

/**
 * A new array in the chosen order.
 *
 * Copied rather than sorted in place: the input is React Query's cached
 * array, and sorting it where it lies mutates the cache. The list would then
 * appear already sorted on the next render and the change would not show.
 */
export function sortItems(items: CollectionItem[], sort: CollectionSort): CollectionItem[] {
  return [...items].sort(COMPARATORS[sort]);
}
